import { Code as Key } from 'constants/keyboard';
import { WIDGET_MENU_OPTIONS } from 'types/WidgetMenu.types';
import { AccessibilityViewIndex } from 'types/accessibilityViewIndex';

import { ElementsPackTypes } from '../components/WidgetMenu/SideMenu/ElementsMenu/ElementsMenu.types';

export type RecommendedIconPack = {
  // Currently only support icons8
  // TODO support other packs
  type: 'icons8' | 'icons8Illustration';

  // The filter to use for the icon pack - varies based on type
  // Icons8 icons - uses `platform` param in icons8 request
  // Icons8 illustration - uses `styles` param in vector request
  // In-house - uses `iconPacks` algolia facetFilter
  filter: string;
  isMono: boolean;
};

export interface EditorSettingsState {
  zoom: number;
  isSlideView: boolean;
  accessibilityViewIndex: AccessibilityViewIndex;
  /**
   * Apply different visual simulator filter to page canvas, default to none.
   */
  colorVisionMode: ColorVisionMode;

  // Set to TRUE if the right side menu widget settings is open
  isWidgetSettingsView: boolean;

  // current left menu opened;
  activeWidgetMenu: WIDGET_MENU_OPTIONS;
  // the tab index of the replace widget menu
  replaceWidgetMenuIndex: number;

  // ElementsPackTypes
  elementsWidgetMenuIndex: ElementsPackTypes | null;

  hasKeyboardIgnore: boolean;
  allowedKeyboardKeys: Key[];
  downloadModal: { isOpen: boolean; trigger: DownloadModalTrigger };
  originalEditorId?: string;
  chartWidgetSettingsTabIndex: number;

  /**
   * flag to indicate language set by user manually once
   */
  languageUpdatedOnce: boolean;

  /**
   * The brand index indicating the current brand used on the editor
   * For example,
   *  all brands ['Brand 1', 'Brand 2', 'Brand 3', 'Brand 4]
   * Then this index 2 means 'Brand 3'
   */
  brandIdx: number;

  // Flag to indicate if the keyboard shortcut modal for browser specific copy/paste is open
  keyboardShortcutModalOpen: boolean;

  // Flag to indicate if the grid should be shown on the Page
  hasShowGrid: boolean;
  // Flag to indicate if the margin should be shown on the Page
  hasShowMargin: boolean;
  // Flag to indicate if the print bleed should be shown on the Page
  hasShowPrintBleed: boolean;

  // RTC is enabled for team sharing
  isTeamSharingEnabled: boolean;
  showCollaborationWarning: boolean;

  // Flag to indicate if the editor is uploading a file
  isUploading: boolean;

  // Flag to indicate if the edit background menu is open,
  // and which tab opened, color or pattern
  editBackgroundView?: EditBackgroundType;

  // Flag to indicate if alt text tag should be shown on canvas when widgets are selected
  showAltTagOnCanvas: boolean;

  // Flag to indicate if alt text has been edited at least once
  hasEditedAltText: boolean;

  // Flag to indicate if the font upload modal is open
  isGatedFontUploadModalOpen: boolean;

  // Flag to indicate if the signature modal is open
  isGatedSignatureModalOpen: boolean;

  // Flag to indicate if the widget control should be hidden
  forceHideWidgetControl: boolean;

  // Flag to indicate if the user is zooming with scroll
  isScrollZooming: boolean;

  // Flag to indicate if the flow mode is enabled for all widgets (not just connected)
  isFlowModeEnabled: boolean;

  // Flag to indicate if the page number toolbar is open
  isPageNumberToolbarOpen: boolean;

  // Flag for combined upload and image panels
  imagePanelTabIndex: number;

  // Pack to show in recommendation section of icon widget menu/replace
  recommendedIconPack: RecommendedIconPack | null;

  // Enabled interaction submission
  enabledInteractionSubmission: boolean;
  // Flag to indicate if the design preview modal is open
  isPreviewModalOpen: boolean;
  // Flag to indicate if the interactive menu is open
  isInteractiveMenuOpen: boolean;
  // Flag to indicate if the user is dragging over the canvas
  isDragOverActive: boolean;
}

export enum DownloadModalTrigger {
  Default = '',
  Navbar = 'Navbar',
  ShareButton = 'ShareButton',
  AccessibilityMenu = 'AccessibilityModal',
  AccessibilityMenuDownloadButton = 'AccessibilityMenuDownloadButton',
}

export enum ColorVisionMode {
  none = 'none',
  achromatopsia = 'achromatopsia',
  deuteranopia = 'deuteranopia',
  protanopia = 'protanopia',
  tritanopia = 'tritanopia',
  cataracts = 'cataracts',
  lowvision = 'lowvision',
}

export enum ReplaceMenuTabIndex {
  ICON = 0,
  PHOTO = 1,
  UPLOAD = 2,
  BRANK_KIT = 3,
}

export type EditBackgroundType = 'color' | 'pattern' | undefined;
